import { useState } from 'react';
import type { AlertRow } from '@glyph/protocol-ts';
import { useAlertChannels, useAlerts, useDevices } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  Section,
  SegmentedControl,
  Tag,
  severityTone,
} from '@glyph/ui';
import { fmtTs } from './shared';
import { PushSetupCard } from './PushSetupCard';
import { RoutingCard } from './RoutingCard';

/** Alerts: list with severity tags + Close, Open/All, push setup, Routing (email through Resend, webhooks; the OLED is fixed). */
export function AlertsScreen() {
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const alerts = useAlerts(filter === 'open');
  const devices = useDevices();
  const nameOf = (id: string) => devices.data?.find((d) => d.id === id)?.name ?? 'Platform';
  const list = alerts.data ?? [];
  const openCount = list.filter((a) => !a.acked_at).length;
  const channels = useAlertChannels();
  const routedTo = (channels.data ?? []).filter((c) => c.enabled && !c.disabled_at).map((c) => c.kind.toUpperCase());
  const row = (a: AlertRow) => (
    <div
      key={a.id}
      className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t border-line px-4 py-[13px] md:flex-nowrap"
    >
      <span className="font-mono text-sm text-fg4 tabular">{fmtTs(a.ts)}</span>
      <span className="max-w-[180px] truncate text-md font-medium text-fg">{nameOf(a.device_id)}</span>
      <div className="flex min-w-0 flex-1 basis-[240px] items-center gap-2.5">
        <Tag tone={severityTone(a.severity)}>{a.severity}</Tag>
        <span className="text-md font-normal leading-[1.4] text-fg">
          {a.detail}
          {a.count > 1 ? <span className="meta ml-2 text-fg4">×{a.count}</span> : null}
          {a.state === 'firing' ? <span className="meta ml-2 text-bad">FIRING</span> : null}
        </span>
      </div>
      {a.acked_at ? (
        <Tag>CLOSED</Tag>
      ) : (
        <Button size="xs" mono onClick={() => void alerts.ack(a)} aria-label="Close alert">
          CLOSE
        </Button>
      )}
    </div>
  );
  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Alerts"
        meta={`${openCount} OPEN · ROUTED TO ${[...new Set(routedTo)].join(' AND ') || 'CONSOLE ONLY'}`}
        actions={
          <SegmentedControl
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'all', label: 'All' },
            ]}
          />
        }
      />
      <div className="md:hidden">
        <SegmentedControl
          fullWidth
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'all', label: 'All' },
          ]}
        />
      </div>
      <div className="flex flex-wrap items-start gap-4 md:gap-5">
        <Card className="min-w-0 basis-full md:basis-auto" style={{ flex: '1 1 560px' }}>
          {list.length === 0 && (
            <EmptyState
              label={filter === 'open' ? 'No open alerts' : 'No alerts'}
              hint="Device-detected alerts arrive immediately; offline and stale-telemetry checks run every 15 s."
            />
          )}
          {list.map(row)}
        </Card>
        <div className="flex min-w-0 basis-full flex-col gap-4 md:min-w-[280px] md:basis-auto md:gap-5" style={{ flex: '0 1 320px' }}>
          <PushSetupCard />
          <RoutingCard />
        </div>
      </div>
    </Section>
  );
}
